import * as React from 'react';

import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Tooltip, TooltipPlacement } from '@shared/components/bootstrap';
import { AddressDetailBuildingRestrictionsAnswer } from '@shared/types/address_detail';
import { AddressDetailField } from '@shared/types/address_detail';
import { IAddressDetail } from '@shared/types/address_detail';
import {
  buildBuildingRestrictionHourOptions,
  buildingRestrictionTimeToMinutes,
} from '@shared/utils/building_restriction_time';

import { RadioFormGroup } from '../radio_form_group';
import { SelectFormGroup } from '../select_form_group';

const OPTIONS = [
  { label: 'Yes', value: AddressDetailBuildingRestrictionsAnswer.Yes },
  { label: 'No', value: AddressDetailBuildingRestrictionsAnswer.No },
  { label: 'I don’t know', value: AddressDetailBuildingRestrictionsAnswer.Unknown },
];

const UNKNOWN = 'unknown';

const OPENING_HOURS: Hour[] = [
  ...buildBuildingRestrictionHourOptions(6, 15).map(({ name, value }) => ({ label: name, value })),
  { label: 'I don’t know', value: UNKNOWN },
];

const CLOSING_HOURS: Hour[] = [
  ...buildBuildingRestrictionHourOptions(6, 19).map(({ name, value }) => ({ label: name, value })),
  { label: 'I don’t know', value: UNKNOWN },
];

const RESTRICTIONS_TITLE =
  'Some buildings have restrictions for when moving companies can enter the building or\
use the service elevator / loading dock. This is especially common in large apartment complexes or offices.\
Please contact your building management to see if there are any restrictions in your building.';

type Hour = {
  label: string;
  value: number | 'unknown';
};

const BuildingHourSelector: React.FC<{
  selection?: number | null;
  label: React.ReactNode;
  hours: Hour[];
  min?: number;
  max?: number;
  onSelect(value: number | undefined): void;
}> = ({ min = 5, max = 20, selection, onSelect, label, hours }) => (
  <SelectFormGroup
    options={hours.filter(({ value }) => {
      if (value === UNKNOWN) {
        return true;
      }

      const timeInMinutes = buildingRestrictionTimeToMinutes(value);
      const maxInMinutes = max === undefined ? undefined : buildingRestrictionTimeToMinutes(max);
      const minInMinutes = min === undefined ? undefined : buildingRestrictionTimeToMinutes(min);

      return (
        (maxInMinutes === undefined || timeInMinutes < maxInMinutes) &&
        (minInMinutes === undefined || timeInMinutes > minInMinutes)
      );
    })}
    label={label}
    value={selection ?? UNKNOWN}
    onChange={(value) => onSelect(value === UNKNOWN ? undefined : value)}
  />
);

export const Restrictions: React.FC<{
  details: IAddressDetail;
  initialBuildingRestrictionsAnswer?: string | null;
  error?: string;
  onDetail(details: IAddressDetail): void;
}> = ({ details, onDetail, error }) => {
  let restrictionsMessage = '';

  if (details[AddressDetailField.Elevator] && details[AddressDetailField.LoadingDock]) {
    restrictionsMessage = ', use the elevator, or use the loading dock';
  } else if (details[AddressDetailField.Elevator]) {
    restrictionsMessage = ' or use the elevator';
  } else if (details[AddressDetailField.LoadingDock]) {
    restrictionsMessage = ' or use the loading dock';
  }
  const label = (
    <>
      Does your building have restrictions on when Clutter can enter the building{restrictionsMessage}?{' '}
      <Tooltip title={RESTRICTIONS_TITLE} placement={TooltipPlacement.Bottom}>
        <FontAwesomeIcon icon={faQuestionCircle} />
      </Tooltip>{' '}
    </>
  );

  return (
    <>
      <RadioFormGroup
        name="building-restrictions"
        label={label}
        options={OPTIONS}
        value={details[AddressDetailField.BuildingRestrictionsAnswer]}
        error={!details[AddressDetailField.BuildingRestrictionsAnswer] ? error : undefined}
        onChange={(value) =>
          onDetail({
            ...details,
            [AddressDetailField.BuildingOpeningHour]: null,
            [AddressDetailField.BuildingClosingHour]: null,
            [AddressDetailField.BuildingRestrictionsAnswer]: value,
          })
        }
      />

      {details[AddressDetailField.BuildingRestrictionsAnswer] === AddressDetailBuildingRestrictionsAnswer.Yes && (
        <div className="form-group">
          <label>
            When is the earliest time that your building will let us start and the latest time that we can finish?
          </label>
          <div className="row">
            <div className="col-md-6">
              <BuildingHourSelector
                label="Earliest possible appointment start:"
                hours={OPENING_HOURS}
                selection={details[AddressDetailField.BuildingOpeningHour]}
                max={details[AddressDetailField.BuildingClosingHour] || undefined}
                onSelect={(value) =>
                  onDetail({
                    ...details,
                    [AddressDetailField.BuildingOpeningHour]: value,
                  })
                }
              />
            </div>
            <div className="col-md-6">
              <BuildingHourSelector
                label="Latest possible appointment end:"
                hours={CLOSING_HOURS}
                selection={details[AddressDetailField.BuildingClosingHour]}
                min={details[AddressDetailField.BuildingOpeningHour] || undefined}
                onSelect={(value) =>
                  onDetail({
                    ...details,
                    [AddressDetailField.BuildingClosingHour]: value,
                  })
                }
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};
